<template>
  <div class="space-y-4">
    <BaseCard>
      <BaseCardBody clear-padding>
        <dl>
          <div class="px-4 py-5 sm:px-6 border-b sm:grid sm:grid-cols-4 sm:gap-4 sm:items-center">
            <dt class="text-sm font-medium text-gray-800">Recipient</dt>
          </div>
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-4 sm:gap-4 sm:px-6 sm:items-center">
            <dt class="text-sm font-medium text-gray-500">Contact</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-3 space-y-1">
              <div v-if="contact">
                {{ contact.fullName }}
              </div>
              <div v-else>-</div>
            </dd>
          </div>
          <AccountRelation v-if="accountRelationId" :account-relation-id="accountRelationId" />
          <div
            class="px-4 py-5 sm:px-6 border-b border-t sm:grid sm:grid-cols-4 sm:gap-4 sm:items-center"
          >
            <dt class="text-sm font-medium text-gray-800">Recipient Details</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-3">
              <div class="space-x-2">
                <RegistrationPicker
                  v-if="registrations.length > 0"
                  :options="registrations"
                  @select="setDetailsFromRegistration"
                />
              </div>
            </dd>
          </div>
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-4 sm:gap-4 sm:px-6 sm:items-center">
            <dt class="text-sm font-medium text-gray-500">Name</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-3">
              <ValidationProvider rules="required" name="recipient name" slim vid="recipientName">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormInput v-model="inputRecipientName" class="w-2/3" />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </dd>
          </div>
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-4 sm:gap-4 sm:px-6 sm:items-center">
            <dt class="text-sm font-medium text-gray-500">Address</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-3">
              <ValidationProvider name="recipient address" slim vid="recipientAddress">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormInput v-model="inputRecipientAddress" class="w-2/3" />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </dd>
          </div>
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-4 sm:gap-4 sm:px-6 sm:items-center">
            <dt class="text-sm font-medium text-gray-500">Vat number</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-3">
              <ValidationProvider name="recipient vat number" slim vid="recipientVatNumber">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormInput v-model="inputRecipientVatNumber" class="w-1/2" />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </dd>
          </div>
          <div
            class="bg-white px-4 py-5 sm:grid sm:grid-cols-4 sm:gap-4 sm:px-6 rounded-b sm:items-center"
          >
            <dt class="text-sm font-medium text-gray-500">Vat office</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-3">
              <ValidationProvider name="recipient vat address" slim vid="recipientVatAddress">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormInput v-model="inputRecipientVatOffice" class="w-1/2" />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </dd>
          </div>
        </dl>
      </BaseCardBody>
    </BaseCard>
  </div>
</template>

<script>
import AccountRelation from './AccountRelation.vue';
import RegistrationPicker from './RegistrationPicker.vue';

export default {
  components: {
    AccountRelation,
    RegistrationPicker,
  },
  props: {
    contactId: { type: String, default: null },
    contactRequestId: { type: String, default: null },
    accountRelationId: { type: String, default: null },
    recipientName: { type: String, required: true },
    recipientAddress: { type: String, default: '' },
    recipientVatNumber: { type: String, default: '' },
    recipientVatOffice: { type: String, default: '' },
    canChangeRecipient: Boolean,
    canUpdateRecipient: Boolean,
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    contact() {
      return this.$store.state.entities.contacts.byId[this.contactId];
    },
    contactRequest() {
      return this.$store.state.entities.contactRequests.byId[this.contactRequestId];
    },
    accountRelation() {
      if (typeof this.accountRelationId !== 'string') {
        return null;
      }

      return this.$store.state.entities.accountRelations.byId[this.accountRelationId];
    },
    account() {
      if (!this.accountRelation) {
        return null;
      }

      return (
        this.$store.state.entities.accounts.byId[this.accountRelation.related_account_uuid] || null
      );
    },
    registrations() {
      return (this.account?.registrations || []).map(
        (id) => this.$store.state.entities.accountRegistrations.byId[id]
      );
    },
    inputRecipientName: {
      get() {
        return this.recipientName;
      },
      set(recipientName) {
        this.update('recipient-name', recipientName);
      },
    },
    inputRecipientAddress: {
      get() {
        return this.recipientAddress;
      },
      set(recipientAddress) {
        this.update('recipient-address', recipientAddress);
      },
    },
    inputRecipientVatNumber: {
      get() {
        return this.value?.recipientVatNumber;
      },
      set(recipientVatNumber) {
        this.update('recipient-vat-number', recipientVatNumber);
      },
    },
    inputRecipientVatOffice: {
      get() {
        return this.recipientVatOffice;
      },
      set(recipientVatOffice) {
        this.update('recipient-vat-office', recipientVatOffice);
      },
    },
  },
  created() {
    this.setDefaultDetails();
  },
  methods: {
    update(prop, value) {
      this.$emit(`update:${prop}`, value);
    },
    setLoading(value) {
      this.loading = Boolean(value);
    },
    setDetailsFromRegistration(registration) {
      this.inputRecipientName = registration.businessName;
      this.inputRecipientAddress = registration.businessAddress;
      this.inputRecipientVatNumber = registration.number;
      this.inputRecipientVatOffice = registration.office;
    },
    setDefaultDetails() {
      if (this.inputRecipientName) {
        return;
      }

      if (this.account) {
        this.inputRecipientName = this.account.name;
        return;
      }

      if (this.accountRelation) {
        this.inputRecipientName = this.accountRelation.name;
        return;
      }

      if (this.contact || this.contactRequest) {
        this.inputRecipientName = this.contact?.fullName || this.contactRequest?.fullName;
      }
    },
  },
};
</script>
